import { supporterNoteQuery, useUpdateSupporterNoteMutation } from "@/api/supporter";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Textarea } from "@/components/ui/Textarea";
import { getErrorMessage } from "@/utils/error";
import { Box, Field, Flex, Text, toast } from "@givenwell/components";
import { UpdateSupporterNoteBody } from "@givenwell/management-api";
import { colors } from "@givenwell/theme";
import { useForm } from "@tanstack/react-form";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/admin/supporters/$supporterId/note")({
  component: NotePage,
  beforeLoad: () => ({
    breadcrumb: "Note",
  }),
});

function NotePage() {
  const { supporterId } = Route.useParams();
  const { data: note } = useSuspenseQuery(supporterNoteQuery(supporterId));
  const updateNoteMut = useUpdateSupporterNoteMutation(supporterId);

  const form = useForm({
    defaultValues: {
      note: note?.note || "",
    } satisfies UpdateSupporterNoteBody,

    async onSubmit({ value }) {
      try {
        await updateNoteMut.mutateAsync(value);
        form.reset(value);
      } catch (e) {
        toast.error(getErrorMessage(e));
        console.error(e);
      }
    },
  });

  return (
    <Box css={{ spaceY: 16 }}>
      <Flex css={{ flexDir: "row", gap: 16, flexShrink: 0 }}>
        <Card
          title="Note"
          description="This note is only visible to Givenwell staff, and should never be shared with the supporter."
          css={{ width: 700 }}
        >
          <form
            onSubmit={e => {
              e.preventDefault();
              form.handleSubmit();
            }}
          >
            <Flex css={{ flexDir: "column", gap: 16 }}>
              <form.Field name="note">
                {field => (
                  <Field>
                    <Textarea
                      onValueChange={field.handleChange}
                      value={field.state.value}
                      rows={10}
                      autoFocus
                      style={{ fieldSizing: "content", resize: "none" } as React.CSSProperties}
                      onKeyDown={e => {
                        if (e.key === "s" && (e.metaKey || e.ctrlKey)) {
                          e.preventDefault();
                          form.handleSubmit();
                        }
                      }}
                    />
                    {note?.last_updated_at && note.last_updated_at > "2025-" ? (
                      <Text css={{ color: colors.text2, fontScale: "xs" }}>
                        Last updated by {note?.last_updated_by || "Unknown"} on{" "}
                        {note?.last_updated_at && note.last_updated_at > "2025-"
                          ? new Date(note.last_updated_at).toLocaleDateString()
                          : "Unknown"}
                      </Text>
                    ) : null}
                  </Field>
                )}
              </form.Field>

              <form.Subscribe selector={state => [state.isDirty]}>
                {([isDirty]) => (
                  <Button
                    type="submit"
                    variant="tonal"
                    color="primary"
                    loadingText="Saving..."
                    loading={updateNoteMut.isPending}
                    disabled={!isDirty}
                  >
                    Save Changes
                  </Button>
                )}
              </form.Subscribe>
            </Flex>
          </form>
        </Card>
      </Flex>
    </Box>
  );
}
